import React, { useState, useEffect, useRef } from 'react';
import Draggable from 'react-draggable';
import { Resizable } from 'react-resizable';
import 'react-resizable/css/styles.css';

const DEFAULT_TOP = 50;          // px from top
const DEFAULT_WIDTH_PCT = 0.9;   // 90% of viewport width
const DEFAULT_HEIGHT = 400;      // arbitrary start height
const MIN_WIDTH = 250;
const MIN_HEIGHT = 200;
const MARGIN = 20;               // margin when maximizing

// TODO save column(or maybe the same as assettable) and position settings in local storage
const DraggableResizablePanel = ({ children, onClose }) => {
    // Current size & position
    const [size, setSize] = useState({ width: 800, height: DEFAULT_HEIGHT });
    const [position, setPosition] = useState({ x: 0, y: DEFAULT_TOP });

    // Maximize/restore support
    const [isMaximized, setIsMaximized] = useState(false);
    const prevSizeRef = useRef(size);
    const prevPosRef  = useRef(position);

    // Initialize size/position AFTER mount so window.* is available
    useEffect(() => {
        const width = Math.round(window.innerWidth * DEFAULT_WIDTH_PCT);
        const height = DEFAULT_HEIGHT;
        const x = Math.max((window.innerWidth - width) / 2, 0);
        const y = DEFAULT_TOP;
        setSize({ width, height });
        setPosition({ x, y });
    }, []);

    // Keep constraints responsive when the viewport changes
    useEffect(() => {
        const onWinResize = () => {
            if (isMaximized) {
                // Stay maximized relative to new window size
                setSize({
                    width: window.innerWidth - MARGIN * 2,
                    height: window.innerHeight - MARGIN * 2,
                });
                setPosition({ x: MARGIN, y: MARGIN });
            }
        };
        window.addEventListener('resize', onWinResize);
        return () => window.removeEventListener('resize', onWinResize);
    }, [isMaximized]);

    // Handle panel resize
    const onResize = (event, { size }) => setSize(size);

    // Handle drag updates
    const onDrag = (e, data) => setPosition({ x: data.x, y: data.y });

    // Toggle maximize/restore
    const toggleMaximize = () => {
        if (isMaximized) {
            // Restore previous values
            setSize(prevSizeRef.current);
            setPosition(prevPosRef.current);
            setIsMaximized(false);
        } else {
            // Save current values, then maximize
            prevSizeRef.current = size;
            prevPosRef.current = position;
            setSize({
                width: window.innerWidth - MARGIN * 2,
                height: window.innerHeight - MARGIN * 2,
            });
            setPosition({ x: MARGIN, y: MARGIN });
            setIsMaximized(true);
        }
    };

    return (
        <Draggable
            handle=".drag-handle"
            position={position}
            onDrag={onDrag}
            bounds="parent" // prevent dragging outside the parent container
        >
            <div style={{ position: 'absolute', zIndex: 1000, pointerEvents: 'auto' }}>
                <Resizable
                    width={size.width}
                    height={size.height}
                    onResize={onResize}
                    minConstraints={[MIN_WIDTH, MIN_HEIGHT]}
                    maxConstraints={[window.innerWidth - MARGIN, window.innerHeight - MARGIN]}
                >
                    <div
                        style={{
                            width: size.width,
                            height: size.height,
                            background: 'white',
                            border: '2px solid #0078d4',
                            borderRadius: '8px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                            fontFamily: 'Arial, sans-serif',
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                        }}
                    >
                        {/* Header (drag handle + buttons) */}
                        <div
                            className="drag-handle"
                            style={{
                                background: '#0078d4',
                                color: 'white',
                                padding: '8px 12px',
                                cursor: 'move',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                userSelect: 'none',
                            }}
                        >
                            <span style={{ fontWeight: 'bold' }}>Asset Attributes</span>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                {/* Maximize / Restore */}
                                <button
                                    onClick={toggleMaximize}
                                    style={{
                                        background: 'transparent',
                                        border: 'none',
                                        color: 'white',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        padding: '0 4px',
                                    }}
                                    title={isMaximized ? 'Restore' : 'Maximize'}
                                >
                                    {isMaximized ? '🗗' : '⛶'}
                                </button>

                                {/* Close */}
                                <button
                                    onClick={onClose}
                                    style={{
                                        background: 'transparent',
                                        border: 'none',
                                        color: 'white',
                                        cursor: 'pointer',
                                        fontSize: '16px',
                                        padding: '0 4px',
                                    }}
                                    title="Close"
                                >
                                    ×
                                </button>
                            </div>
                        </div>

                        {/* Body */}
                        <div
                            style={{
                                flex: 1,
                                overflow: 'auto',
                                padding: '12px',
                            }}
                        >
                            {children}
                        </div>
                    </div>
                </Resizable>
            </div>
        </Draggable>
    );
};

export default DraggableResizablePanel;
